import type { ErrorResult, WithApiData } from "Interfaces";
import type { IndexSignature } from "@clintonelec/typescriptutils";
import { NotificationType, notify } from "src/Notifications";
import { isProduction } from "Data/Utils/System";

type RequestType = "DELETE" | "GET" | "POST" | "PATCH";

const baseUrl = isProduction
	? "https://ddns.clintonelectronics.com/api"
	: "https://develop.ddns.clintonelectronics.com/api";

const baseHeaders = {
	"Content-Type": "application/vnd.api+json",
	"Accept": "application/vnd.api+json"
};

export function GET<TReturn = unknown>(path: string, token?: string, params?: IndexSignature<string | number>) {
	return request<TReturn>(path, "GET", undefined, undefined, params, token);
}

export function POST<TReturn = unknown>(path: string, body?: unknown, token?: string) {
	return request<TReturn>(path, "POST", JSON.stringify(body), undefined, undefined, token);
}

export function PATCH<TReturn = unknown>(path: string, token: string, body?: unknown) {
	return request<TReturn>(path, "PATCH", JSON.stringify(body), undefined, undefined, token);
}

export function DELETE<TReturn = void>(
	path: string, token: string, params?: IndexSignature<string | number>, body?: unknown
) {
	return request<TReturn>(path, "DELETE", JSON.stringify(body), undefined, params, token);
}

function request<TReturn>(path: string, method: RequestType, body?: string, headers?: { [key: string]: string },
	query?: IndexSignature<string | number>, token?: string) {
	let url = `${ baseUrl }/${ path }`;

	if (query && Object.keys(query).length != 0) {
		url += `?${ encodeQueryParams(query) }`;
	}

	const authHeaders = { "Authorization": `Bearer ${ token }` };

	return new Promise<WithApiData<TReturn>>((resolve, reject) => {
		return fetch(url, {
			body,
			headers: { ...baseHeaders, ...authHeaders, ...headers },
			method,
			mode: "cors"
		})
			.then((response: Response) => {
				const { status } = response;

				if (status >= 200 && status < 300) {
					const result = status === 204
						? { data: {} }
						: response.json();

					resolve(result);
				} else if (status >= 400 && status < 500) {
					if (status === 401 || status === 429) {
						reject(status);
					} else {
						response.json().then((errorResult: ErrorResult) => {
							reject(JSON.stringify(errorResult?.errors));
						});
					}
				} else if (status >= 500 && status < 600) {
					notify(NotificationType.ERROR, "Error", "An unknown error has occurred");
					reject();
				}
			}, (error) => {
				console.log(error);
			})
			.catch((error) => {
				console.log(error);
			});
	});
}

function encodeQueryParams(params: IndexSignature<string | number>) {
	return Object.keys(params)
		.map((key) => {
			const encKey = encodeURIComponent(key);
			const encParam = encodeURIComponent(params[key]);

			return `${ encKey }=${ encParam }`;
		})
		.join("&");
}
